import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Search, ArrowRight, ChevronRight, Package, ShieldCheck } from 'lucide-react';
import { CategoryCard, SourcingState, ProductCard, ExportBadge, enquiryUrl } from '../components/catalogue/CatalogueParts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const subSlug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.06 } } };
const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } } };

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [taxonomy, setTaxonomy] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([axios.get(`${BACKEND_URL}/api/catalogue/taxonomy`), axios.get(`${BACKEND_URL}/api/products`)])
      .then(([t, p]) => { setTaxonomy(t.data); setProducts(p.data); })
      .catch((error) => console.error('Error fetching catalogue:', error))
      .finally(() => setLoading(false));
  }, []);

  const rawCategory = searchParams.get('category') || '';
  const typeParam = searchParams.get('type') || '';
  const subParam = searchParams.get('sub') || '';

  // Resolve slug; legacy values (Audio / Computing / Mobile / Wearables) map to Consumer Technology subcategories
  const resolved = useMemo(() => {
    if (!rawCategory || taxonomy.length === 0) return { category: null, sub: subParam };
    const direct = taxonomy.find((c) => c.slug === rawCategory || c.name.toLowerCase() === rawCategory.toLowerCase());
    if (direct) return { category: direct, sub: subParam };
    const consumer = taxonomy.find((c) => c.slug === 'consumer-technology');
    const legacySub = (consumer?.subcategories || []).find((s) => s.toLowerCase() === rawCategory.toLowerCase());
    return legacySub ? { category: consumer, sub: legacySub } : { category: null, sub: '' };
  }, [rawCategory, subParam, taxonomy]);
  const category = resolved.category;
  const activeSub = resolved.sub;

  const setParams = (next) => {
    const params = {};
    Object.keys(next).forEach((k) => { if (next[k]) params[k] = next[k]; });
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const selectCategory = (slug) => setParams({ category: slug });
  const selectSub = (sub) => setParams({ category: category.slug, sub });
  const selectType = (type) => setParams({ category: category.slug, type: typeParam === type ? '' : type });

  const query = searchQuery.trim().toLowerCase();
  const matches = (p) => !query || p.name.toLowerCase().includes(query) || p.description.toLowerCase().includes(query);
  const collectionProducts = category
    ? products.filter((p) => p.category_slug === category.slug && (!activeSub || p.subcategory === activeSub) && (!typeParam || p.product_type === typeParam) && matches(p))
    : [];
  const searchResults = !category && query ? products.filter(matches) : [];
  const activeFamily = category && typeParam ? (category.families || []).find((f) => f.slug === typeParam) : null;
  const families = category ? (category.families || []) : [];
  const subcategories = category ? (category.subcategories || null) : null;
  const subCounts = category ? (category.subcategory_counts || null) : null;

  return (
    <main className="pt-32 pb-20" data-testid="products-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-10">
          <span className="inline-block px-4 py-2 rounded-full bg-[#0096D6]/10 text-[#0096D6] font-manrope text-sm font-medium mb-4">Product Catalogue</span>
          <h1 className="font-outfit text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-testid="products-heading">Technology Hardware</h1>
          <p className="font-manrope text-slate-600 text-lg max-w-2xl">
            Explore high-performance computing, enterprise components and consumer technology available through our international wholesale network.
          </p>
        </motion.div>

        {/* Primary category navigation — hardware first, consumer last */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="flex flex-col gap-5 mb-10">
          <div className="flex flex-wrap gap-2" data-testid="category-nav">
            <button type="button" onClick={() => setParams({})} className={`px-5 py-2.5 rounded-full font-manrope text-sm font-medium transition-colors duration-300 ${!category ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-400'}`} data-testid="category-nav-overview">Overview</button>
            {taxonomy.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => selectCategory(c.slug)}
                className={`px-5 py-2.5 rounded-full font-manrope text-sm font-medium transition-colors duration-300 ${category?.slug === c.slug ? 'bg-[#0096D6] text-white shadow-lg' : c.slug === 'consumer-technology' ? 'bg-slate-50 text-slate-500 border border-slate-200 hover:border-slate-400' : 'bg-white text-slate-600 border border-slate-200 hover:border-[#0096D6] hover:text-[#0096D6]'}`}
                data-testid={`category-nav-${c.slug}`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input type="text" placeholder={category ? `Search ${category.name}...` : 'Search listed products...'} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-full bg-slate-100 border border-slate-200 focus:outline-none focus:border-[#0096D6] font-manrope" data-testid="product-search-input" />
          </div>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (<div key={i} className="animate-pulse"><div className="aspect-[4/3] bg-slate-200 rounded-2xl mb-4" /><div className="h-4 bg-slate-200 rounded w-3/4 mb-2" /><div className="h-4 bg-slate-200 rounded w-1/2" /></div>))}
          </div>
        ) : !category ? (
          /* ---------- Overview ---------- */
          <div data-testid="category-overview">
            {query ? (
              <div className="mb-12">
                <p className="font-manrope text-sm text-slate-500 mb-4" data-testid="search-results-label">Currently listed products matching “{searchQuery}” — {searchResults.length} result{searchResults.length === 1 ? '' : 's'}</p>
                {searchResults.length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-slate-200 rounded-2xl"><Package className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="font-manrope text-slate-500">No listed products match. Enterprise hardware is sourced to order — open a category to request availability.</p></div>
                ) : (
                  <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {searchResults.map((p) => (<motion.div key={p.id} variants={itemVariants}><ProductCard product={p} category={taxonomy.find((c) => c.slug === p.category_slug)} /></motion.div>))}
                  </motion.div>
                )}
              </div>
            ) : null}
            <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {taxonomy.map((c, i) => (<motion.div key={c.slug} variants={itemVariants}><CategoryCard category={c} index={i} onSelect={selectCategory} /></motion.div>))}
            </motion.div>
          </div>
        ) : (
          /* ---------- Collection ---------- */
          <div data-testid={`collection-${category.slug}`}>
            <nav className="flex items-center gap-2 font-manrope text-sm text-slate-500 mb-6" data-testid="breadcrumb">
              <button type="button" onClick={() => setParams({})} className="hover:text-[#0096D6]">Products</button>
              <ChevronRight className="w-4 h-4" />
              <span className="text-slate-900 font-medium" data-testid="breadcrumb-category">{category.name}</span>
              {(activeFamily || activeSub) && (<><ChevronRight className="w-4 h-4" /><span className="text-slate-900 font-medium" data-testid="breadcrumb-leaf">{activeFamily ? activeFamily.name : activeSub}</span></>)}
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-start mb-10">
              <div>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <h2 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900" data-testid="collection-title">{category.name}</h2>
                  {category.export_controlled && <ExportBadge testId="collection-export-badge" />}
                </div>
                <p className="font-manrope text-slate-600 text-lg max-w-3xl">{category.description}</p>
                {category.export_controlled && (
                  <p className="font-manrope text-sm text-slate-500 mt-3 flex items-start gap-2 max-w-3xl" data-testid="collection-export-notice"><ShieldCheck className="w-4 h-4 text-[#0096D6] shrink-0 mt-0.5" />{category.export_notice}</p>
                )}
              </div>
              <div className="w-full lg:w-56 aspect-[4/3] bg-white rounded-2xl border border-slate-200/80 p-4 hidden lg:block">
                <img src={category.image} alt={category.name} className="w-full h-full object-contain" />
              </div>
            </div>

            {families.length > 0 && (
              <div className="mb-8">
                <p className="font-manrope text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-3">Product families</p>
                <div className="flex flex-wrap gap-2" data-testid="family-chips">
                  {families.map((f) => (
                    <button key={f.slug} type="button" onClick={() => selectType(f.slug)} className={`px-3.5 py-1.5 rounded-full border font-manrope text-xs font-medium transition-colors duration-300 ${typeParam === f.slug ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-[#0096D6]/50 hover:text-[#0096D6]'}`} data-testid={`family-chip-${f.slug}`}>{f.name}</button>
                  ))}
                </div>
              </div>
            )}

            {subcategories && (
              <div className="flex flex-wrap gap-2 mb-8" data-testid="subcategory-filters">
                <button type="button" onClick={() => selectSub('')} className={`px-4 py-2 rounded-full font-manrope text-sm font-medium transition-colors duration-300 ${!activeSub ? 'bg-[#0096D6] text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-[#0096D6]'}`} data-testid="sub-filter-all">All</button>
                {subcategories.map((s) => (
                  <button key={s} type="button" onClick={() => selectSub(s)} className={`px-4 py-2 rounded-full font-manrope text-sm font-medium transition-colors duration-300 ${activeSub === s ? 'bg-[#0096D6] text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-[#0096D6]'}`} data-testid={`sub-filter-${subSlug(s)}`}>
                    {s}{subCounts ? <span className="ml-1.5 text-[10px] opacity-70">{subCounts[s] || 0}</span> : null}
                  </button>
                ))}
              </div>
            )}

            {collectionProducts.length === 0 ? (
              category.listed_count === 0 || !subcategories ? (
                <SourcingState category={category} type={typeParam} />
              ) : (
                <div className="text-center py-16 border border-dashed border-slate-200 rounded-3xl" data-testid="subcategory-empty-state">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">No listed products in {activeSub || category.name}{query ? ` matching “${searchQuery}”` : ''}</h3>
                  <p className="font-manrope text-slate-500 mb-5">Lines not shown are sourced on request.</p>
                  <Link to={enquiryUrl(category, null, activeSub ? { sub: activeSub } : null)} className="btn-primary inline-flex items-center gap-2" data-testid="request-availability-btn">Request Availability <ArrowRight className="w-4 h-4" /></Link>
                </div>
              )
            ) : (
              <>
                <p className="font-manrope text-xs text-slate-400 uppercase tracking-[0.14em] mb-4" data-testid="listed-products-label">Currently listed products — {collectionProducts.length}</p>
                <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {collectionProducts.map((p) => (<motion.div key={p.id} variants={itemVariants}><ProductCard product={p} category={category} /></motion.div>))}
                </motion.div>
                <div className="mt-12 rounded-2xl border border-slate-200 bg-slate-50/70 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="collection-enquiry-cta">
                  <p className="font-manrope text-slate-600">{category.sourcing}</p>
                  <Link to={enquiryUrl(category, typeParam, activeSub ? { sub: activeSub } : null)} className="btn-primary inline-flex items-center gap-2 whitespace-nowrap" data-testid="request-availability-btn">Request Availability <ArrowRight className="w-4 h-4" /></Link>
                </div>
              </>
            )}
          </div>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mt-20 text-center">
          <p className="font-manrope text-slate-600 mb-4">Looking for specific products or larger quantities?</p>
          <Link to={category ? enquiryUrl(category, typeParam) : '/contact#enquiry-form'} className="btn-primary inline-flex items-center gap-2" data-testid="products-contact-cta">
            Send Your Requirements <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </main>
  );
}
